import React from "react";

import { api } from "api";
import { castDoc, castDocs } from "utils/helpers";
import { Iterable, IPlace } from "types";

import { useToast } from "../use-toast";

export const useCollectedPlaces = () => {
  const { handleFirebaseError } = useToast();

  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<Iterable<IPlace>[]>([]);

  const fetchCollectedPlaces = React.useCallback(
    async (countries: string[], tags: string[]) => {
      try {
        setLoading(true);

        const responses = await api.firestore.collectedPlaces.list(
          countries,
          tags
        );

        const documents = responses
          .map((response) => castDocs<Iterable<IPlace>>(response))
          .flat(1);

        setData(documents);
      } catch (error) {
        handleFirebaseError(error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { loading, data, fetch: fetchCollectedPlaces } as const;
};

type Place = Iterable<IPlace>;

export const useCollectedPlace = ({ id }: { id: string }) => {
  const { handleFirebaseError } = useToast();

  const [loading, setLoading] = React.useState(false);
  const [document, setDocument] = React.useState<Place | undefined>(undefined);

  const fetchPlace = React.useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.firestore.collectedPlaces.get(id);

      if (response.exists()) {
        const document = castDoc<Iterable<IPlace>>(response);
        setDocument(document);
      } else {
        setDocument(undefined);
      }
    } catch (error) {
      setDocument(undefined);
      handleFirebaseError(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  React.useEffect(() => {
    void fetchPlace();
  }, [fetchPlace]);

  return { loading, place: document, refresh: fetchPlace } as const;
};

export * from "./use-collect-logs";
export * from "./use-interval-countries-counter";
